import { Add, Done } from '@mui/icons-material';
import { Button, ButtonProps, Popover, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, JSXElementConstructor, useRef, useState } from 'react';
import { Expression, FilterConfiguration } from '../types';
import { expressionValidator, getDefaultExpression } from '../utils';
import { ExpressionField } from './ExpressionField';

type Props = {
  filters: FilterConfiguration[];
  initialExpression: Expression | null;
  onSubmit: (expression: Expression) => void;
  AnchorComponent: JSXElementConstructor<{ expression: Expression; onClick: () => void; open: boolean }>;
};

export const FilterEditor = ({ filters, initialExpression, onSubmit, AnchorComponent }: Props) => {
  const defaultExpression = initialExpression ?? getDefaultExpression(filters[0]);

  const [open, setOpen] = useState<boolean>(false);
  const [expression, setExpression] = useState(defaultExpression);
  const anchorRef = useRef<HTMLDivElement>(null);

  const filter = filters.find(({ filterKey }) => filterKey === expression.field) as FilterConfiguration;
  const hasValue = !!expression.value;
  const validation = expressionValidator(filter, expression.operation, expression.value);
  const errorMessage = validation === true ? undefined : validation;
  const expressionValid = hasValue && errorMessage === undefined;

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();

    if (!expressionValid) return;

    onSubmit(expression);
    setOpen(false);
  };

  const toggleOpen = () => {
    const closing = open;
    setOpen((o) => !o);
    if (closing) {
      setExpression(defaultExpression);
    }
  };

  const submitProps: Partial<ButtonProps> = initialExpression
    ? { startIcon: <Done />, 'aria-label': t('a11y.common.filters.updateFilter'), children: t('save') }
    : { startIcon: <Add />, 'aria-label': t('a11y.common.filters.addFilter'), children: t('add') };

  return (
    <>
      <div ref={anchorRef}>
        <AnchorComponent onClick={toggleOpen} expression={expression} open={open} />
      </div>

      <Popover
        open={open}
        onClose={toggleOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        anchorEl={anchorRef.current}
        slotProps={{ paper: { sx: { p: 1.5, mt: 1, width: '40.625rem' } } }}
      >
        <Stack direction="row" alignItems="start" component="form" role="search" onSubmit={handleSubmit}>
          <ExpressionField expression={expression} onChange={setExpression} filters={filters} errorMessage={errorMessage} />
          <Button type="submit" variant="outlined" disabled={!expressionValid} sx={{ ml: 1, mt: 2, height: '2rem' }} {...submitProps} />
        </Stack>
      </Popover>
    </>
  );
};
